/**
 * Preview-only money path so the isolated Preview build can run a 10-person
 * split without calling a gateway. ToursService reaches this module only after
 * the installed build identity and a narrow fixture-ID policy both agree.
 *
 * Survives a full document load: share URLs are real `/split/{token}` links,
 * so each payer arrives in a new JS realm. Memory alone would lose the board.
 */
import { guestChargeCents, splitMarketplaceCents } from '../payments/processor-fee'
import { splitShareUrl } from '../payments/checkout-origin'
import { chargeIntentFromCreatePayment } from '../payments/charge-result'
import { isPreviewMoneyFixtureIdentifier } from './preview-money-policy'
import type {
  TourPayInput,
  TourPayResult,
  TourSplitInfo,
  TourSplitPayInput,
  TourSplitParticipantStatus,
  TourSplitResult,
  TourSplitShare,
} from '../types/tours.types'

const STORAGE_KEY = 'tago.preview.tours-split.v1'

const splits = new Map<string, TourSplitResult>()
const shareIndex = new Map<string, { bookingId: string; info: TourSplitInfo }>()
let hydrated = false

type ShareEntry = { bookingId: string; info: TourSplitInfo }

export type PreviewSplitSnapshot = {
  splits: [string, TourSplitResult][]
  shares: [string, ShareEntry][]
}

export function serializePreviewSplits(): PreviewSplitSnapshot {
  return {
    splits: Array.from(splits.entries()),
    shares: Array.from(shareIndex.entries()),
  }
}

export function loadPreviewSplitSnapshot(snapshot: PreviewSplitSnapshot): void {
  splits.clear()
  shareIndex.clear()
  for (const [id, result] of snapshot.splits ?? []) {
    if (typeof id === 'string' && result && Array.isArray(result.shares)) {
      splits.set(id, result)
    }
  }
  for (const [token, entry] of snapshot.shares ?? []) {
    if (typeof token === 'string' && entry?.bookingId && entry.info) {
      shareIndex.set(token, entry)
    }
  }
  hydrated = true
}

function previewStorage(): Storage | null {
  try {
    if (typeof sessionStorage === 'undefined') return null
    return sessionStorage
  } catch {
    return null
  }
}

function persistPreviewSplits(): void {
  const storage = previewStorage()
  if (!storage) return
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(serializePreviewSplits()))
  } catch {
    // Private mode / quota — preview still works for the current realm.
  }
}

function hydratePreviewSplits(): void {
  if (hydrated && splits.size > 0) return
  if (hydrated && shareIndex.size > 0) return
  const storage = previewStorage()
  if (!storage) {
    hydrated = true
    return
  }
  try {
    const raw = storage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as PreviewSplitSnapshot
      loadPreviewSplitSnapshot(parsed)
      return
    }
  } catch {
    // Corrupt payload — start empty.
  }
  hydrated = true
}

export function isPreviewToursMoney(id: string): boolean {
  return isPreviewMoneyFixtureIdentifier(id)
}

export function previewCreateSplit(
  bookingId: string,
  participantCount: number,
  totalCents: number,
  meta: { title: string; originLabel: string; destinationLabel: string; startsAt: string },
): TourSplitResult {
  hydratePreviewSplits()
  const existing = splits.get(bookingId)
  if (existing) return existing

  const amounts = splitMarketplaceCents(totalCents, participantCount)
  const shares: TourSplitShare[] = amounts.map((amountCents, index) => {
    const shareToken = `preview-share-${String(index + 1).padStart(2, '0')}`
    const share: TourSplitShare = {
      amountCents,
      shareToken,
      shareUrl: splitShareUrl(shareToken),
      email: null,
      status: 'pending',
      sortPosition: index + 1,
    }
    shareIndex.set(shareToken, {
      bookingId,
      info: {
        shareToken,
        amountCents,
        totalCents,
        status: 'pending',
        position: index + 1,
        totalParticipants: amounts.length,
        passengerCount: participantCount,
        startsAt: meta.startsAt,
        title: meta.title,
        originLabel: meta.originLabel,
        destinationLabel: meta.destinationLabel,
      },
    })
    return share
  })

  const result: TourSplitResult = { bookingId, totalCents, shares }
  splits.set(bookingId, result)
  persistPreviewSplits()
  return result
}

export function peekPreviewSplit(bookingId: string): TourSplitResult | null {
  hydratePreviewSplits()
  return splits.get(bookingId) ?? null
}

export function previewSplitInfo(token: string): TourSplitInfo | null {
  hydratePreviewSplits()
  if (token === 'preview-token') {
    const first = shareIndex.get('preview-share-01')
    if (first) return first.info
  }
  return shareIndex.get(token)?.info ?? null
}

function last4Fail(cardNumber?: string): boolean {
  const digits = (cardNumber ?? '').replace(/\D/g, '')
  return digits.endsWith('0002') || digits.endsWith('0341')
}

export async function previewPayTour(input: TourPayInput): Promise<TourPayResult> {
  if (input.card && last4Fail(input.card.cardNumber)) {
    throw Object.assign(new Error('card_declined'), { code: 'TOURS_PAYMENT_FAILED' })
  }
  if (input.provider === 'paypal') {
    return chargeIntentFromCreatePayment({
      paymentId: 'pay_preview_paypal',
      transactionId: 'PAYPAL-PREVIEW',
      provider: 'paypal',
      status: 'pending',
      approvalUrl: 'https://www.sandbox.paypal.com/checkoutnow?token=PREVIEW',
      checkoutMode: 'paypal_app_switch',
    })
  }
  return chargeIntentFromCreatePayment({
    paymentId: 'pay_preview_card',
    transactionId: 'WOMPI-PREVIEW',
    provider: 'wompi',
    status: 'captured',
  })
}

export async function previewPaySplit(input: TourSplitPayInput): Promise<TourPayResult> {
  hydratePreviewSplits()
  const entry = shareIndex.get(input.token)
  if (!entry) {
    throw Object.assign(new Error('not found'), { code: 'TOURS_SPLIT_NOT_FOUND' })
  }
  if (input.card && last4Fail(input.card.cardNumber)) {
    throw Object.assign(new Error('card_declined'), { code: 'TOURS_PAYMENT_FAILED' })
  }

  if (input.provider === 'paypal') {
    return chargeIntentFromCreatePayment({
      paymentId: `pay_preview_${input.token}`,
      transactionId: `PAYPAL-${input.token}`,
      provider: 'paypal',
      status: 'pending',
      approvalUrl: 'https://www.sandbox.paypal.com/checkoutnow?token=PREVIEW',
      checkoutMode: 'paypal_app_switch',
    })
  }

  markShareSettled(input.token)
  return chargeIntentFromCreatePayment({
    paymentId: `pay_preview_${input.token}`,
    transactionId: `TX-${input.token}`,
    provider: 'wompi',
    status: 'captured',
  })
}

export function markShareSettled(token: string): void {
  hydratePreviewSplits()
  const entry = shareIndex.get(token)
  if (!entry) return
  entry.info = { ...entry.info, status: 'settled' }
  const split = splits.get(entry.bookingId)
  if (!split) return
  splits.set(entry.bookingId, {
    ...split,
    shares: split.shares.map((share) =>
      share.shareToken === token
        ? { ...share, status: 'settled' as TourSplitParticipantStatus }
        : share,
    ),
  })
  persistPreviewSplits()
}

export function previewGuestCharge(amountCents: number, method: 'card' | 'paypal'): number {
  return guestChargeCents({ marketplaceCents: amountCents, method }).chargeCents
}

/** Test-only: empty the in-memory + serialized preview split board. */
export function resetPreviewSplitsForTests(): void {
  splits.clear()
  shareIndex.clear()
  hydrated = true
  const storage = previewStorage()
  try {
    storage?.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
}
